import { FormEvent, useEffect, useState } from 'react';
import type { CashDirection, MeResponse, RegisterRef, ShiftSummary } from '@kassa/shared';
import { heldList } from '../held';
import { api, ApiError } from '../api';
import { formatMoney, hasPermission } from '../format';

interface Props {
  me: MeResponse;
  onBack: () => void;
}

// Kassa növbəsi: aç, nağd mədaxil/məxaric, X hesabatı, bağla (sayılan nağd, fərq, Z nömrəsi).
export function Shift({ me, onBack }: Props) {
  const [registers, setRegisters] = useState<RegisterRef[]>([]);
  const [registerId, setRegisterId] = useState('');
  const [shift, setShift] = useState<ShiftSummary | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openingCash, setOpeningCash] = useState('0');
  const [direction, setDirection] = useState<CashDirection>('in');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [counted, setCounted] = useState('');
  const [closed, setClosed] = useState<ShiftSummary | null>(null);

  const canCash = hasPermission(me, 'cash.in_out');

  useEffect(() => {
    api
      .shiftRegisters()
      .then((r) => {
        setRegisters(r);
        if (r.length > 0) setRegisterId(r[0].id);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Kassalar yüklənmədi'));
  }, []);

  useEffect(() => {
    if (!registerId) return;
    setLoaded(false);
    setClosed(null);
    api
      .shiftCurrent(registerId)
      .then((s) => setShift(s))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Növbə yüklənmədi'))
      .finally(() => setLoaded(true));
  }, [registerId]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  const open = (e: FormEvent) => {
    e.preventDefault();
    run(async () => setShift(await api.shiftOpen({ registerId, openingCash: Number(openingCash) })));
  };

  const cash = (e: FormEvent) => {
    e.preventDefault();
    if (!shift) return;
    run(async () => {
      await api.shiftCash(shift.shiftId, { direction, amount: Number(amount), note: note.trim() || null });
      setAmount('');
      setNote('');
      setShift(await api.shiftX(shift.shiftId));
    });
  };

  const refreshX = () => {
    if (!shift) return;
    run(async () => setShift(await api.shiftX(shift.shiftId)));
  };

  const close = (e: FormEvent) => {
    e.preventDefault();
    if (!shift) return;
    // Gözləyən çek varsa xəbərdarlıq (KT-101): onlar satılmayıb, növbəyə düşmür
    const held = heldList(registerId).length;
    if (held > 0 && !window.confirm(`Bu kassada ${held} gözləyən çek var — satılmayıb. Növbə yenə də bağlansın?`)) return;
    run(async () => {
      const result = await api.shiftClose(shift.shiftId, { countedCash: Number(counted) });
      setClosed(result);
      setShift(null);
      setCounted('');
    });
  };

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn back-home" onClick={onBack}>
          ← Ana səhifə
        </button>
        <span className="brand">Kassa növbəsi</span>
      </header>
      <main className="page">
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}

        <section className="card">
          <label className="field">
            Kassa
            <select value={registerId} onChange={(e) => setRegisterId(e.target.value)} disabled={busy || registers.length === 0}>
              {registers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.outletName} · {r.code} {r.name}
                </option>
              ))}
            </select>
          </label>
          {registers.length === 0 && <p className="muted">Bu obyektdə aktiv kassa yoxdur.</p>}
        </section>

        {closed && (
          <section className="card" aria-live="polite">
            <h2>Növbə bağlandı — Z {closed.zNumber}</h2>
            <p>
              Gözlənən nağd {formatMoney(closed.expectedCash)} · sayılan {formatMoney(closed.countedCash)} · fərq{' '}
              <strong className={closed.difference !== null && closed.difference < 0 ? 'bad' : ''}>{formatMoney(closed.difference)}</strong>
            </p>
            <p className="muted small">
              Nisyə ödənişi (nağd mədaxilə daxildir): nağd {formatMoney(closed.debtCash)} · kart {formatMoney(closed.debtCard)}
            </p>
            <ShiftMethods s={closed} />
          </section>
        )}

        {loaded && !shift && registerId && (
          <section className="card">
            <h2>Açıq növbə yoxdur</h2>
            {hasPermission(me, 'shift.manage') && (
              <form onSubmit={open} className="form-grid">
                <label>
                  Açılış nağdı
                  <input type="number" min="0" step="0.01" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)} />
                </label>
                <button type="submit" className="btn-primary" disabled={busy}>
                  Növbəni aç
                </button>
              </form>
            )}
          </section>
        )}

        {shift && (
          <>
            <section className="card">
              <h2>
                {shift.registerCode} · növbə açıqdır
              </h2>
              <p>
                Açılış {formatMoney(shift.openingCash)} · mədaxil {formatMoney(shift.cashIn)} · məxaric {formatMoney(shift.cashOut)}
              </p>
              <p className="muted small">
                Nisyə ödənişi (nağd mədaxilə daxildir): nağd {formatMoney(shift.debtCash)} · kart {formatMoney(shift.debtCard)}
              </p>
              <p>
                <strong>Gözlənən nağd {formatMoney(shift.expectedCash)}</strong>
              </p>
              <ShiftMethods s={shift} />
              <button type="button" className="btn-small" onClick={refreshX} disabled={busy}>
                X hesabatı (yenilə)
              </button>
            </section>

            {canCash && (
              <section className="card">
                <h2>Nağd mədaxil / məxaric</h2>
                <form onSubmit={cash} className="form-grid">
                  <label>
                    Növ
                    <select value={direction} onChange={(e) => setDirection(e.target.value as CashDirection)}>
                      <option value="in">Mədaxil</option>
                      <option value="out">Məxaric</option>
                    </select>
                  </label>
                  <label>
                    Məbləğ
                    <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
                  </label>
                  <label>
                    Səbəb
                    <input type="text" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
                  </label>
                  <button type="submit" className="btn-small" disabled={busy || !amount}>
                    Yaz
                  </button>
                </form>
              </section>
            )}

            {hasPermission(me, 'shift.manage') && (
              <section className="card">
                <h2>Növbəni bağla</h2>
                <form onSubmit={close} className="form-grid">
                  <label>
                    Sayılan nağd
                    <input type="number" min="0" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} required />
                  </label>
                  <button type="submit" className="btn-primary" disabled={busy || counted === ''}>
                    Bağla
                  </button>
                </form>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

/** X/Z: növbədəki satış ödəniş növləri üzrə (KT-90) — satış − qaytarma. Bonus pul deyil, nisyə və köçürmə kassaya girmir. */
function ShiftMethods({ s }: { s: ShiftSummary }) {
  const m = s.sales;
  if (!m) return null;
  const rows: [string, string][] = [
    ['Nağd', m.cash],
    ['Kart', m.card],
    ['Köçürmə', m.transfer],
    ['Bonus', m.bonus],
    ['Nisyə', m.credit],
  ];
  return (
    <table className="report-table shift-methods" aria-label="Ödəniş növləri">
      <caption>
        Satış ödəniş növləri üzrə — {m.count} çek{m.returns > 0 ? `, ${m.returns} qaytarma` : ''} (xalis)
      </caption>
      <tbody>
        {rows.map(([label, v]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td className="num">{formatMoney(Number(v))}</td>
          </tr>
        ))}
        <tr className="total-row">
          <th scope="row">Cəmi</th>
          <td className="num">{formatMoney(Number(m.total))}</td>
        </tr>
      </tbody>
    </table>
  );
}
